import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { useMemo } from 'react';

import { LeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useSymbols } from '@flipster/features/src/shared/symbol/use-symbols';
import { Badge } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { marketsBunja } from '@flipster/socket/r230522/market';
import { spotMarketsBunja } from '@flipster/socket/r230522/spot-market';
import { css } from '@flipster/styled-system/css';
import { grid, hstack, vstack } from '@flipster/styled-system/patterns';
import { Usd1Currency } from '@flipster/utils/constants/currency';

const TradeSection = () => {
  const isVaraUser = useCheckSpotOnly();

  return (
    <section className={css({ mt: 48 })}>
      <div className={vstack({ gap: 8, alignItems: 'flex-start' })}>
        <div className={hstack({ gap: 8 })}>
          <h3 className={css({ textStyle: 'Title/M' })}>Trade</h3>
          <Badge type='tint' color='promotion'>
            Flipster bonus points
          </Badge>
        </div>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          Get Flipster exclusive bonus points when you trade.
        </p>
      </div>
      {!isVaraUser && <PerpetualList />}
      <SpotList />
    </section>
  );
};

export default TradeSection;

const PerpetualList = () => {
  const { symbols } = useSymbols('perpetual');
  const { marketsAtom } = useBunja(marketsBunja);
  const perpMarket = useAtomValue(marketsAtom);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { isMobile } = useBreakpoint();

  const items = useMemo(() => {
    if (!symbols?.marketCap) return [];
    return symbols.marketCap.filter((symbol) => {
      const item = instrumentsKeyBySymbol?.[symbol];
      if (!item) return false;
      return item.quoteCurrency === Usd1Currency;
    });
  }, [symbols?.marketCap, instrumentsKeyBySymbol]);

  if (items.length === 0) return null;

  return (
    <div className={grid({ columns: { base: 1, tablet: 2 }, mt: 8, gridColumnGap: 40 })}>
      <GridHeader title='Perpetual' />
      {items.length >= 2 && !isMobile ? <GridHeader title='Perpetual' /> : <div />}
      {items.map((item) => (
        <SymbolItem
          key={item}
          symbol={item}
          price={perpMarket?.[item].midPrice}
          priceChangePct24h={perpMarket?.[item].priceChangePct24h}
          leverage={instrumentsKeyBySymbol?.[item].maxLeverageHardLimit}
        />
      ))}
    </div>
  );
};

const SpotList = () => {
  const { symbols } = useSymbols('spot');
  const { spotMarketsAtom } = useBunja(spotMarketsBunja);
  const spotMarket = useAtomValue(spotMarketsAtom);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { isMobile } = useBreakpoint();

  const items = useMemo(() => {
    if (!symbols?.marketCap) return [];
    return symbols.marketCap.filter((symbol) => {
      const item = instrumentsKeyBySymbol?.[symbol];
      if (!item) return false;
      return item.baseCurrency === Usd1Currency;
    });
  }, [symbols?.marketCap, instrumentsKeyBySymbol]);

  if (items.length === 0) return null;

  return (
    <div className={grid({ columns: { base: 1, tablet: 2 }, mt: 24, gridColumnGap: 40 })}>
      <GridHeader title='Spot' />
      {items.length >= 2 && !isMobile ? <GridHeader title='Spot' /> : <div />}
      {items.map((item) => (
        <SymbolItem
          key={item}
          symbol={item}
          price={spotMarket?.[item].midPrice}
          priceChangePct24h={spotMarket?.[item].priceChangePct24h}
          isSpot
        />
      ))}
    </div>
  );
};

const GridHeader = ({ title }: { title: string }) => {
  return (
    <div
      className={hstack({
        justifyContent: 'space-between',
        textStyle: 'Text/S/Regular',
        borderBottom: '1px solid',
        borderColor: 'border.default.level-1',
        color: 'content.default.level-2',
        py: 8,
      })}
    >
      <div>{title}</div>
      <div>Price</div>
    </div>
  );
};

interface SymbolItemProps {
  symbol: string;
  price?: string;
  priceChangePct24h?: string;
  leverage?: number;
  isSpot?: boolean;
}

const SymbolItem = ({ symbol, price, priceChangePct24h, leverage, isSpot }: SymbolItemProps) => {
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const instrument = instrumentsKeyBySymbol?.[symbol];
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  return (
    <Link
      href={isSpot ? `/trade/spot/${symbol}` : `/trade/perpetual/${symbol}`}
      className={hstack({
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'border.default.level-1',
        py: 16,
        color: 'content.default.level-4',
      })}
      onClick={() => {
        setOrderAttribution(OrderAttributionMapper.usd1Points.usd1PointsPage);
      }}
    >
      <SymbolDisplay
        symbol={symbol}
        icon={<SymbolDisplay.Icon size={24} space={8} />}
        mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
        subLabel={
          leverage &&
          !isSpot && (
            <div className={css({ mt: 4 })}>
              <LeverageBadge leverage={leverage} />
            </div>
          )
        }
      />
      <div className={vstack({ alignItems: 'flex-end', gap: 2 })}>
        <Formatted.Number
          value={price}
          precision={instrument?.pricePrecision}
          toFixed
          withComma
          css={{ textStyle: 'Text/L/Regular' }}
        />
        <Formatted.Percent
          value={priceChangePct24h}
          precision={2}
          toFixed
          withColor
          withArrow
          css={{ textStyle: 'Text/M/Regular' }}
        />
      </div>
    </Link>
  );
};
